import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2, Check, PiggyBank, Plus, Wallet } from 'lucide-react';
import { CountUp, EASE_OUT, Reveal, Stagger, StaggerItem } from './motion-primitives';

type T = (key: any) => string;

/* ── Visuels ─────────────────────────────────────────────────────── */

function WealthVisual({ t }: { t: T }) {
  const rows = [
    { icon: Wallet, label: t('home.whatIs.bank'), value: '21 200 €', pct: 60 },
    { icon: PiggyBank, label: t('home.whatIs.savings'), value: '9 600 €', pct: 27 },
    { icon: Building2, label: t('home.whatIs.realEstate'), value: '4 650 €', pct: 13 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {t('home.mock.totalWealth')}
        </p>
        <p className="mt-2 text-4xl font-semibold tracking-tight">
          <CountUp to={35450} /> <span className="text-2xl text-muted-foreground">€</span>
        </p>
      </div>
      <div className="space-y-4">
        {rows.map((row, i) => (
          <motion.div
            key={row.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: i * 0.08, ease: EASE_OUT }}
            className="flex items-center gap-3"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <row.icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between">
                <span className="truncate text-sm font-medium">{row.label}</span>
                <span className="text-sm tabular-nums text-muted-foreground">{row.value}</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: EASE_OUT }}
                  style={{ width: `${row.pct}%`, transformOrigin: 'left' }}
                  className="h-full rounded-full bg-primary"
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function BudgetVisual({ t }: { t: T }) {
  const rows = [
    { label: t('home.mock.budget.food'), spent: 320, max: 400 },
    { label: t('home.mock.budget.transport'), spent: 85, max: 150 },
    { label: t('home.mock.budget.shopping'), spent: 180, max: 200 },
    { label: t('home.mock.budget.leisure'), spent: 260, max: 250 },
  ];

  const tone = (ratio: number) =>
    ratio > 1 ? 'bg-destructive' : ratio > 0.85 ? 'bg-amber-400' : 'bg-primary';

  return (
    <div className="space-y-5">
      <div className="flex items-baseline justify-between">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {t('home.mock.monthlyBudget')}
        </p>
        <p className="text-sm font-medium tabular-nums">2 000 €</p>
      </div>
      {rows.map((row, i) => {
        const ratio = row.spent / row.max;
        return (
          <motion.div
            key={row.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: i * 0.07, ease: EASE_OUT }}
          >
            <div className="flex items-baseline justify-between text-sm">
              <span>{row.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {row.spent} / {row.max} €
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.07, ease: EASE_OUT }}
                style={{ width: `${Math.min(ratio, 1) * 100}%`, transformOrigin: 'left' }}
                className={`h-full rounded-full ${tone(ratio)}`}
              />
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function GoalVisual({ t }: { t: T }) {
  const radius = 68;
  const circumference = 2 * Math.PI * radius;
  const progress = 0.75;

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative">
        <svg width="176" height="176" viewBox="0 0 176 176" className="-rotate-90">
          <circle
            cx="88"
            cy="88"
            r={radius}
            fill="none"
            strokeWidth="10"
            className="stroke-muted"
          />
          <motion.circle
            cx="88"
            cy="88"
            r={radius}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            className="stroke-primary"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: circumference * (1 - progress) }}
            transition={{ duration: 1.4, ease: EASE_OUT }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-semibold tabular-nums">
            <CountUp to={75} suffix=" %" />
          </span>
          <span className="mt-1 text-xs text-muted-foreground">
            {t('home.mock.notification.reached')}
          </span>
        </div>
      </div>
      <p className="mt-6 text-sm font-medium">{t('home.mock.notification.goal')}</p>
      <p className="mt-1 text-sm tabular-nums text-muted-foreground">6 000 / 8 000 €</p>
    </div>
  );
}

function CategoriesVisual({ t }: { t: T }) {
  const rows = [
    { label: t('home.mock.budget.food'), color: '#16c47f', budget: '400 €' },
    { label: t('home.mock.budget.transport'), color: '#0ea5e9', budget: '150 €' },
    { label: t('home.mock.budget.leisure'), color: '#f59e0b', budget: '250 €' },
    { label: t('home.story.4.custom'), color: '#8b5cf6', budget: '120 €', custom: true },
  ];

  return (
    <div className="space-y-3">
      <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        {t('home.story.4.visualLabel')}
      </p>
      {rows.map((row, i) => (
        <motion.div
          key={row.label}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: i * 0.08, ease: EASE_OUT }}
          className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
            row.custom ? 'border-primary/40 bg-primary/5' : 'border-border'
          }`}
        >
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: row.color }}
          />
          <span className="flex-1 truncate text-sm font-medium">{row.label}</span>
          {row.custom && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:text-brand-400">
              {t('home.story.4.badge')}
            </span>
          )}
          <span className="text-sm tabular-nums text-muted-foreground">{row.budget}</span>
        </motion.div>
      ))}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground"
      >
        <Plus className="h-4 w-4" />
        {t('home.story.4.add')}
      </button>
    </div>
  );
}

/* ── Section ─────────────────────────────────────────────────────── */

export function StickyStory({ t }: { t: T }) {
  const [active, setActive] = useState(0);
  const titleRefs = useRef<(HTMLHeadingElement | null)[]>([]);

  /**
   * Le chapitre actif est le dernier dont le TITRE a franchi la ligne de lecture.
   * Un seul calcul pour toute la colonne : monotone avec le scroll, donc jamais
   * de course entre observateurs — et la bascule tombe au moment où l'œil arrive
   * sur le nouveau titre, pas sur la marge qui le précède.
   */
  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const readingLine = window.innerHeight * 0.5;
      let next = 0;

      titleRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= readingLine) next = i;
      });

      setActive((current) => (current === next ? current : next));
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const chapters = [
    {
      title: t('home.whatIs.block1.title'),
      desc: t('home.whatIs.block1.desc'),
      bullets: [t('home.story.1.b1'), t('home.story.1.b2'), t('home.story.1.b3')],
    },
    {
      title: t('home.whatIs.block2.title'),
      desc: t('home.whatIs.block2.desc'),
      bullets: [t('home.story.2.b1'), t('home.story.2.b2'), t('home.story.2.b3')],
    },
    {
      title: t('home.feature.goals.title'),
      desc: t('home.feature.goals.desc'),
      bullets: [t('home.story.3.b1'), t('home.story.3.b2'), t('home.story.3.b3')],
    },
    {
      title: t('home.whatIs.block4.title'),
      desc: t('home.whatIs.block4.desc'),
      bullets: [t('home.story.4.b1'), t('home.story.4.b2'), t('home.story.4.b3')],
    },
  ];

  const visuals = [
    <WealthVisual t={t} />,
    <BudgetVisual t={t} />,
    <GoalVisual t={t} />,
    <CategoriesVisual t={t} />,
  ];

  return (
    <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
      {/* Colonne texte */}
      <div>
        {chapters.map((chapter, i) => (
          <Chapter key={chapter.title} active={active === i}>
            <span className="text-xs font-medium tabular-nums text-muted-foreground">0{i + 1}</span>
            <h3
              ref={(el) => (titleRefs.current[i] = el)}
              className="mt-4 text-balance text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {chapter.title}
            </h3>
            <p className="mt-4 max-w-md text-pretty leading-relaxed text-muted-foreground">
              {chapter.desc}
            </p>
            <Stagger className="mt-6 space-y-3" stagger={0.07} delayChildren={0.1}>
              {chapter.bullets.map((bullet) => (
                <StaggerItem key={bullet} as="li" className="flex items-start gap-3 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-muted-foreground">{bullet}</span>
                </StaggerItem>
              ))}
            </Stagger>
            {/* Sur mobile, le visuel accompagne directement son chapitre */}
            <div className="mt-8 flex min-h-[20rem] flex-col justify-center rounded-xl border border-border bg-card p-6 lg:hidden">
              {visuals[i]}
            </div>
          </Chapter>
        ))}
      </div>

      {/* Visuel collant */}
      <div className="hidden lg:block">
        <div className="sticky top-28">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 shadow-sm">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
                className="flex min-h-[20rem] flex-col justify-center"
              >
                {visuals[active]}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex gap-2">
            {chapters.map((chapter, i) => (
              <span
                key={chapter.title}
                className={`h-0.5 flex-1 rounded-full transition-colors duration-300 ${
                  active === i ? 'bg-primary' : 'bg-border'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Chapter({ children, active }: { children: React.ReactNode; active: boolean }) {
  return (
    <div
      className={`border-l py-12 pl-8 transition-colors duration-500 first:pt-0 lg:py-16 ${
        active ? 'border-primary' : 'border-border'
      }`}
    >
      <Reveal>{children}</Reveal>
    </div>
  );
}
